import { Fragment } from 'react'
import { DEPLOYMENT_LABELS, useEnvironment } from '../shell/environment.ts'
import { tenantAddresses } from './slug.ts'

/**
 * Where a tenant's app is published — said once, for every screen that says it.
 *
 * **It names this estate's hostname and no other.** Pre-prod and prod are disjoint estates
 * with their own databases (docs/ENVIRONMENTS.md), so `<slug>.preprod.tasks4staff.com` is
 * not an address of a tenant that exists on prod; it is nothing, or a different company
 * with the same slug. A prod console printing it was stating a falsehood, and stating it on
 * the surface whose actions — retire, mint a superadmin's reset link — cannot be undone.
 *
 * The estate comes from `useEnvironment()`, which reads it from the API's `/health` and
 * **never guesses** (docs/DECISIONS.md, "The environment label comes from the API, not from
 * the build"). Nothing here looks at `location.hostname`: that derives which API to call,
 * not which estate answered.
 *
 * When the estate is `local` or not yet known, both candidates are shown **as candidates**,
 * conditionally worded and attributed to their estates, with the reason this console cannot
 * choose between them. That is the one honest reading available: it asserts nothing, it
 * cannot be misread as "published at both", and it repeats on this row the ignorance the
 * masthead is already admitting.
 */

export type TenantAddressProps = {
  readonly slug: string
  /**
   * `future` for a tenant that does not exist yet — the create form's preview and summary.
   * `bare` for a place that supplies its own words around the address (the hand-off's
   * "Redeemed at" row): the hostname, or both candidates, with no "published at" in front.
   */
  readonly tense?: 'present' | 'future' | 'bare'
}

export function TenantAddress({ slug, tense = 'present' }: TenantAddressProps) {
  const environment = useEnvironment()
  const addresses = tenantAddresses(slug, environment.deployment)
  const verb = tense === 'future' ? 'will be published at' : tense === 'bare' ? '' : 'published at'

  const only = addresses.length === 1 ? addresses[0] : undefined
  if (only !== undefined) {
    return (
      <>
        {verb === '' ? null : `${verb} `}
        <code>{only.hostname}</code>
      </>
    )
  }

  return (
    <>
      {verb === '' ? null : `${verb} `}
      {addresses.map((address, index) => (
        <Fragment key={address.deployment}>
          {index === 0 ? '' : ', or '}
          <code>{address.hostname}</code> on {DEPLOYMENT_LABELS[address.deployment]}
        </Fragment>
      ))}{' '}
      — this console is pointed at {environment.label}, so it cannot say which
    </>
  )
}
